import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { ReactComponent as CardImage } from 'Assets/svg/card.svg';
import logo from 'Assets/logo/logo64.png';
import Button from 'Components/common/Button';
import { purchaseStatusPopupState } from 'Components/common/purchaseStatusPopup';
import profileState from 'Stores/profile';
import userDetail from 'Stores/userDetail';
import * as CONSTANTS from 'Utils/constants';
import makeApi, { URLS } from 'Utils/apiUtil';
import Utils from 'Utils/utils';
import './manageBilling.scss';

const constructedPlanObj = {
  currentPlan: '',
  isFreeTrial: '',
  trialCredits: '',
  currentPlanName: '',
  isAnnualPlan: '',
  savedCard: '',
  planReviews: '',
  noOfSeats: '',
  totalLookups: '',
  planRenewsDate: '',
  planStartDate: '',
};

const stripe = {};

function constructPlanData(planObj) {
  const savedCardValue = profileState.savedCardData;
  const SubscriptionPlanObj = profileState.planDetails;
  let newConstructedPlanObj = {};
  newConstructedPlanObj.savedCard = savedCardValue || '';

  if (planObj) {
    const { currentPlan, isFreeTrial, trialCredits } = planObj;
    const isAnnualPlan = !!(
      planObj.memberSubscription &&
      planObj.memberSubscription.type &&
      planObj.memberSubscription.type === 1
    );
    newConstructedPlanObj = {
      currentPlan,
      isFreeTrial,
      trialCredits,
      isAnnualPlan,
      savedCard: newConstructedPlanObj.savedCard,
    };

    if (SubscriptionPlanObj && SubscriptionPlanObj.currentPlan) {
      newConstructedPlanObj.noOfSeats =
        SubscriptionPlanObj.currentPlan.numberOfSeats;
      newConstructedPlanObj.totalCredits =
        SubscriptionPlanObj.currentPlan.numberOfCredits >= 100000
          ? 'Unlimited'
          : SubscriptionPlanObj.currentPlan.numberOfCredits;

      newConstructedPlanObj.totalLookups =
        SubscriptionPlanObj.currentPlan.emailLookups >= 100000
          ? 'Unlimited'
          : SubscriptionPlanObj.currentPlan.emailLookups;

      const renewalDate =
        SubscriptionPlanObj.currentPlan &&
        SubscriptionPlanObj.currentPlan.renewalDate
          ? SubscriptionPlanObj.currentPlan.renewalDate
          : '';
      newConstructedPlanObj.planRenewsDate =
        renewalDate !== '' ? Utils.getFormattedDate(renewalDate) : 'N0 Plans';

      newConstructedPlanObj.planStartDate = SubscriptionPlanObj.currentPlan
        ?.startDate
        ? Utils.getFormattedDate(SubscriptionPlanObj.currentPlan?.startDate)
        : 'No Plans';

      newConstructedPlanObj.price = SubscriptionPlanObj.currentPlan?.price || 0;
    }
  }

  return newConstructedPlanObj;
}

const ManageBilling = observer(() => {
  const { userInfo } = userDetail;
  const { currentPlanDisplayName } = userInfo;
  const [planData, setPlanData] = useState(constructedPlanObj);

  const isFreeUser = planData.currentPlan === '';

  const updateCardDetails = () => {
    stripe.handler.open({
      name: 'Adapt.io',
      description: 'Add new card',
    });
  };

  // eslint-disable-next-line no-undef
  stripe.handler = StripeCheckout.configure({
    key: CONSTANTS.STRIPE_KEY,
    image: logo,
    color: 'black',
    locale: 'auto',
    panelLabel: 'Add Card',
    zipCode: true,
    billingAddress: true,
    token(token) {
      stripe.sendStripeTokenToServer(token);
    },
  });

  stripe.sendStripeTokenToServer = function(token) {
    // eslint-disable-next-line no-undef
    window.showSpinner = true;

    const config = {
      url: URLS.sendStripeTokenToServer ? URLS.sendStripeTokenToServer : '',
      method: 'POST',
      params: { stripeCardDetails: JSON.stringify(token) },
    };
    makeApi(config)
      .then(response => {
        // console.log('myLeadsList response  ', response)
        if (response !== null && response.data.hasUpdated) {
          profileState.getSavedCard();
          purchaseStatusPopupState.setToastMsg(
            null,
            null,
            null,
            null,
            null,
            'Card updated successfully',
          );
          // eslint-disable-next-line no-undef
          window.showSpinner = false;
        } else if (
          response !== null &&
          response.data !== null &&
          response.data.error === 'address failure'
        ) {
          purchaseStatusPopupState.setToastMsg(
            null,
            null,
            null,
            null,
            null,
            'Card update failed. Address validation failed.',
          );
        } else {
          purchaseStatusPopupState.setToastMsg(
            null,
            null,
            null,
            null,
            null,
            'Card update failed. Please contact us at <a href="mailto:support@adapt.io">support@adapt.io.</a>',
          );
        }
      })
      .catch(error => {
        console.log('myLeadsList error ', error);
      });
  };

  const fetchBillingData = () => {
    profileState.setLoading(true);

    const savedCardPromise = profileState.getSavedCard();
    const planDataPromise = profileState.getSubscriptionPlans();
    Promise.allSettled([savedCardPromise, planDataPromise]).then(() => {
      profileState.setLoading(false);
    });
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  useEffect(() => {
    setPlanData(constructPlanData(userDetail.userInfo));
  }, [
    userDetail.userInfo,
    profileState.savedCardData,
    profileState.planDetails,
  ]);

  return (
    <>
      <div className="subscription-details-title">Subscription Details</div>
      <div className="subscription-details-card">
        <div className="plan-wrapper">
          <div
            className={`left-section${isFreeUser ? ' free-user' : ''}${
              planData.isFreeTrial ? ' free-trial' : ''
            }${planData.savedCard ? ' has-card' : ''}`}
          >
            <div className="plan-details-wrapper">
              <div className="plan-name-wrapper">
                <span className="label">Plan Name:</span>
                <span className="value">
                  {isFreeUser
                    ? 'Forever FREE'
                    : planData.isFreeTrial
                    ? `${currentPlanDisplayName}TRIAL${
                        planData.isAnnualPlan ? '(Annual)' : '(Monthly)'
                      }`
                    : planData.currentPlan}
                </span>
              </div>
              {planData.currentPlan && (
                <span className="plan-status">Active</span>
              )}
            </div>
            {planData.currentPlan !== '' && (
              <div className="plan-period-wrapper">
                <div className="label-value-wrapper">
                  <span className="label">
                    {planData.isFreeTrial
                      ? 'Trial Start Date'
                      : 'Purchase Date'}
                  </span>
                  <span className="value">{planData.planStartDate}</span>
                </div>
                <div className="label-value-wrapper">
                  <span className="label">
                    {planData.isFreeTrial ? 'Trial End Date' : 'Renewal Date'}
                  </span>
                  <span className="value">{planData.planRenewsDate}</span>
                </div>
                {!planData.isFreeTrial && (
                  <div className="label-value-wrapper">
                    <span className="label">Renewal Cycle</span>
                    <span className="value">{Utils.getPlanType()}</span>
                  </div>
                )}
                {planData.price > 0 && (
                  <div className="label-value-wrapper">
                    <span className="label">Price</span>
                    <span className="value">
                      ${Utils.numberFormat(planData.price)}
                    </span>
                  </div>
                )}
              </div>
            )}
            {(planData.isFreeTrial || isFreeUser) && (
              <Link to="/pricing" className="choose-plan">
                Choose Plan
              </Link>
            )}
          </div>
          {planData.savedCard && (
            <div className="card-wrapper">
              <CardImage className="card-image" />
              <div className="card-details">
                <span className="label">Card Details</span>
                <span className="value">****{planData.savedCard}</span>
              </div>
              <Button
                btnText="Update Card Details"
                icon={false}
                type="button"
                className="update-card-details"
                click={updateCardDetails}
              />
            </div>
          )}
        </div>
        <div className="upgrade-plan-wrapper">
          <div className="title-text">Upgrade Plan</div>
          <div className="message">
            Contact <a href="mailto:sales@adapt.io">sales@adapt.io</a> to
            upgrade your plan.
          </div>
        </div>
      </div>
    </>
  );
});

export default ManageBilling;
